import { Product } from "@omni/types/api/product/bff/product.model";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ProductSliderProps } from "@omni/types/components/props/productSlider.props";
import { attempt } from "@omni/utils/function.utils";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import { Spinner } from "@/components/General/Spinner/spinner.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import {
    CartItemModel,
    CartModel
} from "@/redux/modules/api/data/cart/@types/cart.model";
import { CartItemConfigUpdateOrigin } from "@/redux/modules/api/data/cart/@types/cart.types";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import * as CartSelectors from "@/redux/modules/api/data/cart/cart.selectors";
import * as CustomerModule from "@/redux/modules/api/data/customer/customer.module";
import * as StoreDetailsModule from "@/redux/modules/api/data/stores/storeDetails/storeDetails.module";
import * as StoreDetailsSelectors from "@/redux/modules/api/data/stores/storeDetails/storeDetails.selectors";
import { AddToWishlistStatus } from "@/redux/modules/api/data/wishlist/@types/addToWishlistStatus.enum";
import * as WishlistModule from "@/redux/modules/api/data/wishlist/wishlist.module";
import * as ProductDetailModule from "@/redux/modules/value/productDetail/productDetail.module";
import { TProductDetailState } from "@/redux/modules/value/productDetail/productDetail.types";
import { GetStoreStrategy } from "@/services/store.service";
import { toTrackingProduct } from "@/services/tracking/mappers/cartItem.mapper";
import * as CheckoutStepTracker from "@/services/tracking/trackers/tools/checkout/checkoutStep.tracker";
import { getDefaultAddToCartValues } from "@/utils/deliveryMethod";
import { useStoreCode } from "@/utils/hooks/store/useStoreCode.hook";

import { ShoppingCartComponent } from "./shoppingCart.component";

type Props = {
    innerHTML: string;
    cashbackTooltipInfo: string;
    warrantyDepositTooltipInfo: string;
    ccTooltipInfo: string;
    dsvTooltipInfo: string;
    productSliderConfig: string;
};

const updateCartStatus = (isCartLoading: boolean, cart?: CartModel | null) => {
    const element = document.getElementsByClassName("cart__emptyCart");
    if ((cart === null || !hasItems(cart)) && !isCartLoading) {
        if (element && element.length > 0) {
            element[0].classList.remove("d-none");
        }
    } else {
        if (element && element.length > 0) {
            element[0].classList.add("d-none");
        }
    }
};

const hasItems = (cart?: CartModel | null) => {
    return (cart?.items || []).length > 0;
};

const ShoppingCartContainer = ({
    innerHTML,
    ccTooltipInfo,
    dsvTooltipInfo,
    cashbackTooltipInfo,
    warrantyDepositTooltipInfo,
    productSliderConfig
}: Props) => {
    const shoppingCart = useSelector(CartSelectors.getCart);
    const isLoadingCart = useSelector(CartSelectors.isLoadingCart);
    const storeDetails = useSelector(StoreDetailsSelectors.getStoreDetails);
    const isLoadingStore = useSelector(StoreDetailsSelectors.isLoading);
    const isPromotionCodeLoading = useSelector(
        CartSelectors.isLoadingPromotionCode
    );

    const dispatch = useThunkDispatch();

    const storeCode = useStoreCode({ strategy: GetStoreStrategy.Checkout });

    const parsedProductSliderConfig = attempt<ProductSliderProps | null>(
        () => JSON.parse(productSliderConfig),
        null
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        dispatch(CustomerModule.load());
        if (!isLoadingCart) {
            dispatch(CartModule.load());
        }
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (shoppingCart?.storeId) {
            dispatch(StoreDetailsModule.load(shoppingCart.storeId));
        }
    }, [shoppingCart?.storeId]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (shoppingCart?.customerId) {
            CheckoutStepTracker.trackCartStart(shoppingCart);
        }
    }, [shoppingCart?.customerId]);

    useEffect(() => {
        // effect has to run every time
        // due to the innerHTML being overridden every time
        updateCartStatus(isLoadingCart, shoppingCart);
    });

    const removeFromCart = (
        id: string,
        quantityToRemove: number,
        free: boolean
    ) => {
        return dispatch(CartModule.removeFromCart(id, quantityToRemove, free));
    };

    const updateProductDetail = (data: Partial<TProductDetailState>) => {
        return dispatch(ProductDetailModule.update(data));
    };

    const updateCartItem = (
        id: string,
        quantity: number,
        deliveryType: string,
        product?: CartItemModel,
        quantityDelta?: number
    ) => {
        return dispatch(
            CartModule.updateCartItem(
                id,
                quantity,
                deliveryType,
                product,
                quantityDelta,
                undefined,
                { mergeParams: false }
            )
        );
    };

    const updateCartItemConfig = (
        id: string,
        config: Record<string, unknown>,
        product: CartItemModel,
        productSubType: string
    ) => {
        return dispatch(
            CartModule.updateCartItemConfig(
                id,
                product.quantity,
                product.deliveryType,
                config,
                product,
                productSubType,
                CartItemConfigUpdateOrigin.CART_PAGE,
                shoppingCart?.storeId
            )
        );
    };

    const changeStore = (store: string) => {
        return dispatch(CartModule.changeStore(store));
    };

    const addToWishlist = (sku: string) => {
        const product = shoppingCart?.items.find((i) => i.sku === sku);
        if (!product) {
            return Promise.resolve(AddToWishlistStatus.FAILED);
        }

        return dispatch(
            WishlistModule.addProduct(sku, toTrackingProduct(product))
        );
    };

    const removeFromWishlist = (sku: string) => {
        return dispatch(WishlistModule.removeProduct(sku));
    };

    const addPromotionCode = (code: string, bonusCardNumber?: string) => {
        return dispatch(CartModule.addPromotionCode(code, bonusCardNumber));
    };
    const removePromotionCode = (code: string) => {
        return dispatch(CartModule.removePromotionCode(code));
    };
    const onAddAdditionalService = (
        cartItemId: string,
        serviceSku: string,
        product: CartItemModel
    ) => {
        return dispatch(
            CartModule.updateCartItem(
                cartItemId,
                product.quantity,
                product.deliveryType,
                product,
                1,
                [serviceSku]
            )
        );
    };

    const onRemoveAdditionalService = (cartItemId: string) => {
        const quantity = shoppingCart?.items.find((i) => i.id === cartItemId)
            ?.quantity!;
        return dispatch(CartModule.removeFromCart(cartItemId, quantity, false));
    };

    const onAddAccessory = (
        cartItemId: string,
        sku: string,
        product: Product<ProductFieldSet.ProductTile>
    ) => {
        const cartItem = shoppingCart?.items.find((i) => i.id === cartItemId);

        if (!cartItem) {
            return Promise.resolve();
        }

        const { defaultDeliveryMethod } = getDefaultAddToCartValues(
            product,
            shoppingCart?.storeId
        );

        let deliveryType: DeliveryType | null = cartItem.deliveryType;
        if (!product.deliveryTypes.some((d) => d === deliveryType)) {
            deliveryType = defaultDeliveryMethod;
        }

        if (!deliveryType) {
            return Promise.resolve();
        }

        return dispatch(
            CartModule.addProductTileProductToCart(
                {
                    deliveryType: deliveryType,
                    shopId: shoppingCart?.storeId,
                    quantity: 1,
                    sku
                },
                product,
                true
            )
        );
    };

    return (
        <>
            {isLoadingCart && <Spinner />}
            {!isLoadingCart && (
                <>
                    {(shoppingCart === null || !hasItems(shoppingCart)) && (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: innerHTML
                            }}
                        />
                    )}
                    {shoppingCart && hasItems(shoppingCart) && (
                        <ShoppingCartComponent
                            storeDetails={storeDetails}
                            onChangeStore={(sku) => {
                                updateProductDetail({
                                    sku: sku
                                });
                            }}
                            removeFromCart={removeFromCart}
                            updateCartItem={updateCartItem}
                            onAddAdditionalService={onAddAdditionalService}
                            onRemoveAdditionalService={
                                onRemoveAdditionalService
                            }
                            onAddAccessory={onAddAccessory}
                            updateCartItemConfig={updateCartItemConfig}
                            shoppingCart={shoppingCart}
                            loadingPromotionCode={isPromotionCodeLoading}
                            loadingStore={isLoadingStore}
                            changeStore={changeStore}
                            addPromotionCode={addPromotionCode}
                            removePromotionCode={removePromotionCode}
                            ccTooltipInfo={ccTooltipInfo}
                            dsvTooltipInfo={dsvTooltipInfo}
                            addToWishlist={addToWishlist}
                            removeFromWishlist={removeFromWishlist}
                            cashbackTooltipInfo={cashbackTooltipInfo}
                            warrantyDepositTooltipInfo={
                                warrantyDepositTooltipInfo
                            }
                            customerFavoriteStore={storeCode}
                            productSliderConfig={parsedProductSliderConfig}
                        />
                    )}
                </>
            )}
        </>
    );
};

export { ShoppingCartContainer };
